// src/pages/Reports.js
//
// Chunk 9c — PDF + print layout fully rewritten for ink-friendly,
// bank-statement aesthetic. Black-on-white, tight tables, no emoji
// in chrome, no decorative bars or pie charts. Hides $0 categories.
//
// Preserved from previous version:
//   • Data fetching (months, transactions, income)
//   • generateReport() shape — same `reportData` array
//   • Auto-print path from Log
//   • On-screen preview component below the cards
//   • All chrome (Quick action card, Financial summary card, Business
//     expense card)
//
// Rewritten:
//   • handleDownloadFinancialPDF() — clean serif tables, no chrome bars
//   • handleGenerateExpenseReport() — same aesthetic for consistency
//   • The .print-only block — redesigned for browser-print to match PDF
//
import React, { useState, useEffect } from 'react'
import { useOutletContext, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useDataEvent, EVENTS } from '../lib/dataEvents'
import { usePartner } from '../lib/usePartner'
import { useCurrency } from '../lib/CurrencyContext'
import { theme, atoms, firstName } from '../lib/theme'
import { jsPDF } from 'jspdf'

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
export default function Reports() {
  const { formatCurrency } = useCurrency()
  const { partnerId, partnerProfile } = usePartner()
  const ctx = useOutletContext() || {}
  const isMobile = !!ctx.isMobile
  const [searchParams] = useSearchParams()

  const [availableMonths, setAvailableMonths] = useState([])
  const [selectedMonths, setSelectedMonths] = useState([])
  const [reportData, setReportData] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [allTransactions, setAllTransactions] = useState([])
  const [expenseReportMonth, setExpenseReportMonth] = useState(new Date().getMonth() + 1)
  const [expenseReportYear, setExpenseReportYear] = useState(new Date().getFullYear())
  const [generatingExpenseReport, setGeneratingExpenseReport] = useState(false)
  const [autoPrintTriggered, setAutoPrintTriggered] = useState(false)

  const now = new Date()
  const currentYear = now.getFullYear()
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2]

  useEffect(() => { fetchAvailableMonths() }, [partnerId]) // eslint-disable-line
  // Refetch the available months when underlying data changes
  useDataEvent(
    [
      EVENTS.TRANSACTION_SAVED,
      EVENTS.TRANSACTION_UPDATED,
      EVENTS.TRANSACTION_DELETED,
      EVENTS.BUDGET_SAVED,
      EVENTS.CATEGORY_SAVED,
      EVENTS.CATEGORY_UPDATED,
      EVENTS.CATEGORY_DELETED,
      EVENTS.INCOME_SAVED,
      EVENTS.INCOME_UPDATED,
      EVENTS.INCOME_DELETED,
    ],
    () => {
      fetchAvailableMonths()
      // If a report is already generated, regenerate to reflect changes
      if (selectedMonths.length > 0) generateReport()
    }
  )

  const fetchAvailableMonths = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]
    const [budgetsRes, txRes] = await Promise.all([
      supabase.from('budgets').select('id, month, year')
        .in('user_id', userIds).eq('is_active', true).order('year').order('month'),
      supabase.from('transactions')
        .select('*, budget_categories(name, icon), payment_methods(name, last_four)')
        .in('user_id', userIds)
        .order('transaction_date', { ascending: false })
        .limit(500)
    ])
    if (budgetsRes.data) {
      setAvailableMonths(budgetsRes.data)
      // Default selection: last 3 months that have budgets
      setSelectedMonths(budgetsRes.data.slice(-3).map(b => b.id))
    }
    if (txRes.data) setAllTransactions(txRes.data)
    setLoading(false)
  }

  const toggleMonth = (id) => setSelectedMonths(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id])

  const getMonthlyIncome = (s) => {
    const a = parseFloat(s.amount || 0)
    switch (s.frequency) {
      case 'weekly': return a * 4.33
      case 'biweekly': return a * 2.17
      case 'quarterly': return a / 3
      case 'annual': return a / 12
      default: return a
    }
  }

  const generateReport = async () => {
    if (!selectedMonths.length) return
    setGenerating(true)
    const { data: { user } } = await supabase.auth.getUser()
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]

    // Global household category lookup — resolves each transaction's category to
    // a canonical "parent::child" name. Categories are recreated with a fresh id
    // every month, so a transaction's category_id often points to a DIFFERENT
    // budget generation than the one being reported. Matching by canonical name
    // (not exact UUID) is what makes multi-transaction categories sum correctly.
    const { data: allCatsData } = await supabase.from('budget_categories')
      .select('id, name, parent_id').in('user_id', userIds)
    const allCatsById = {}
    ;(allCatsData || []).forEach(c => { allCatsById[c.id] = c })
    const canonOf = (catId) => {
      const c = allCatsById[catId]
      if (!c) return null
      const parent = c.parent_id ? allCatsById[c.parent_id] : null
      const pn = parent ? (parent.name || '').toLowerCase().trim() : ''
      return `${pn}::${(c.name || '').toLowerCase().trim()}`
    }

    const results = await Promise.all(selectedMonths.map(async (budgetId) => {
      const budget = availableMonths.find(b => b.id === budgetId)
      if (!budget) return null
      const ms = String(budget.month).padStart(2, '0')
      const lastDay = new Date(budget.year, budget.month, 0).getDate()
      const [cR, tR, iR] = await Promise.all([
        supabase.from('budget_categories').select('*').eq('budget_id', budgetId).order('sort_order'),
        // FULL month of household expenses — filtered ONLY by user + date + type,
        // NOT by budget_id, so every transaction is counted regardless of which
        // budget row (or none) it was logged against. This is the actual fix.
        supabase.from('transactions').select('id, amount, category_id, transaction_date')
          .in('user_id', userIds).eq('type', 'expense')
          .gte('transaction_date', `${budget.year}-${ms}-01`).lte('transaction_date', `${budget.year}-${ms}-${lastDay}`),
        supabase.from('income_sources').select('*').in('user_id', userIds).eq('is_active', true).eq('month', budget.month).eq('year', budget.year)
      ])
      const cats = cR.data || [], txs = tR.data || [], income = iR.data || []

      // Sum ALL month expenses (this is the true total, incl. uncategorized), and
      // bucket by canonical category name for the per-category breakdown.
      const spentByCanon = {}
      let totalSpentAll = 0
      txs.forEach(t => {
        const amt = parseFloat(t.amount || 0)
        totalSpentAll += amt
        const canon = canonOf(t.category_id)
        if (canon) spentByCanon[canon] = (spentByCanon[canon] || 0) + amt
      })
      const spent = (cat) => spentByCanon[canonOf(cat.id)] || 0

      const parents = cats.filter(c => !c.parent_id)
      const children = (pid) => cats.filter(c => c.parent_id === pid)
      const catData = parents.map((cat) => {
        const ch = children(cat.id)
        const catSpent = ch.length ? ch.reduce((s, c) => s + spent(c), 0) : spent(cat)
        const catLimit = ch.length ? ch.reduce((s, c) => s + parseFloat(c.limit_amount || 0), 0) : parseFloat(cat.limit_amount || 0)
        return {
          ...cat, spent: catSpent, limit: catLimit,
          children: ch.map(c => ({ ...c, spent: spent(c), limit: parseFloat(c.limit_amount || 0) })),
        }
      }).filter(c => c.spent > 0 || c.limit > 0)
      // True month total (all expenses), not the sum of the categorized rows — so
      // uncategorized / unmatched spending is never silently dropped.
      const totalSpent = totalSpentAll
      const totalIncome = income.reduce((s, i) => s + getMonthlyIncome(i), 0)
      return {
        budgetId, month: budget.month, year: budget.year,
        monthName: MONTH_NAMES[budget.month - 1],
        totalSpent, totalIncome, net: totalIncome - totalSpent,
        catData, txCount: txs.length,
        income: income.map(i => ({ ...i, monthly: getMonthlyIncome(i) })),
        hasBudget: catData.some(c => c.limit > 0)
      }
    }))
    setReportData(results.filter(Boolean).sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month))
    setGenerating(false)
  }

  // Auto-preview path: when arriving from Log's "Print month review" button.
  useEffect(() => {
    if (autoPrintTriggered) return
    if (loading) return
    const monthParam = searchParams.get('month')
    const printParam = searchParams.get('print')
    if (monthParam !== 'current' || printParam !== 'auto') return
    const today = new Date()
    const cur = availableMonths.find(b =>
      b.month === today.getMonth() + 1 && b.year === today.getFullYear()
    )
    if (!cur) {
      setAutoPrintTriggered(true)
      return
    }
    setSelectedMonths([cur.id])
    setAutoPrintTriggered(true)
    // Clear the query params so user isn't stuck in auto-preview state
    window.history.replaceState({}, '', '/report')
    setTimeout(() => {
      generateReport().then(() => {
        setTimeout(() => handlePreviewFinancialPDF(), 200)
      })
    }, 50)
  }, [loading, availableMonths, searchParams, autoPrintTriggered]) // eslint-disable-line

  const getBusinessTransactionsForPeriod = () => {
    return allTransactions.filter(tx => {
      if (!tx.is_business) return false
      const txDate = new Date(tx.transaction_date + 'T12:00:00')
      return txDate.getMonth() + 1 === expenseReportMonth && txDate.getFullYear() === expenseReportYear
    }).sort((a, b) => new Date(a.transaction_date) - new Date(b.transaction_date))
  }

  const loadImageAsBase64 = async (url) => {
    try {
      const response = await fetch(url)
      if (!response.ok) return null
      const blob = await response.blob()
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = () => resolve(null)
        reader.readAsDataURL(blob)
      })
    } catch {
      return null
    }
  }

  // ─── Financial summary PDF — clean serif, ink-friendly ────
  // Aesthetic: bank statement / accountant letter.
  // Black on white, no decorative bars, no emoji, hide $0 lines,
  // category sections that stay together across page breaks.
  const handleDownloadFinancialPDF = async () => {
    if (!reportData.length) return
    const { jsPDF } = await import('jspdf')
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' })
    const pageW = doc.internal.pageSize.getWidth()
    const pageH = doc.internal.pageSize.getHeight()
    const margin = 56
    const contentW = pageW - margin * 2

    const partnerName = partnerProfile?.full_name?.split(' ')[0]
    const reportPeople = partnerName ? `${partnerName} & You` : 'Personal'
    const reportMonthsStr = reportData.map(m => `${m.monthName} ${m.year}`).join(', ')
    const dateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })

    // Helpers
    const setBlack = () => doc.setTextColor(0, 0, 0)
    const setDim   = () => doc.setTextColor(110, 110, 110)
    const setQuiet = () => doc.setTextColor(160, 160, 160)
    const hr = (y, weight = 0.5) => {
      doc.setDrawColor(0, 0, 0); doc.setLineWidth(weight)
      doc.line(margin, y, pageW - margin, y)
    }
    const newPageIfNeeded = (y, neededHeight) => {
      if (y + neededHeight > pageH - margin) {
        addFooter(doc.internal.getNumberOfPages())
        doc.addPage()
        return margin
      }
      return y
    }
    const addFooter = (pageNum) => {
      setQuiet()
      doc.setFont('times', 'italic'); doc.setFontSize(8)
      doc.text('Merq Vault — getmerq.com', margin, pageH - 30)
      doc.text(`Page ${pageNum}`, pageW - margin, pageH - 30, { align: 'right' })
      doc.text('Observations only — not financial advice', pageW / 2, pageH - 30, { align: 'center' })
    }

    // ─── Title block ──────────────────────────────────────
    let y = margin

    setBlack()
    doc.setFont('times', 'bold'); doc.setFontSize(20)
    doc.text('Financial Summary', margin, y)
    y += 24

    doc.setFont('times', 'normal'); doc.setFontSize(11)
    setDim()
    doc.text(reportMonthsStr, margin, y)
    y += 14
    doc.text(`Prepared for ${reportPeople} · ${dateStr}`, margin, y)
    y += 22

    hr(y, 1)
    y += 18

    // ─── Summary table (one row per month) ────────────────
    setBlack()
    doc.setFont('times', 'bold'); doc.setFontSize(10)
    doc.text('SUMMARY', margin, y)
    y += 14

    // Column layout for summary
    const sumCols = {
      month:  margin,
      income: margin + contentW * 0.36,
      spent:  margin + contentW * 0.56,
      net:    margin + contentW * 0.76,
      rate:   pageW - margin,
    }

    // Header row
    setDim()
    doc.setFont('times', 'normal'); doc.setFontSize(9)
    doc.text('Period',        sumCols.month,  y)
    doc.text('Income',        sumCols.income, y, { align: 'right' })
    doc.text('Expenses',      sumCols.spent,  y, { align: 'right' })
    doc.text('Net',           sumCols.net,    y, { align: 'right' })
    doc.text('Savings Rate',  sumCols.rate,   y, { align: 'right' })
    y += 6
    hr(y, 0.4)
    y += 14

    // Body rows
    setBlack()
    doc.setFont('times', 'normal'); doc.setFontSize(11)
    reportData.forEach(m => {
      doc.text(`${m.monthName} ${m.year}`, sumCols.month, y)
      doc.text(formatCurrency(m.totalIncome), sumCols.income, y, { align: 'right' })
      doc.text(formatCurrency(m.totalSpent),  sumCols.spent,  y, { align: 'right' })
      const netStr = (m.net >= 0 ? '+' : '') + formatCurrency(m.net)
      doc.text(netStr, sumCols.net, y, { align: 'right' })
      const rate = m.totalIncome > 0
        ? `${Math.round((1 - m.totalSpent / m.totalIncome) * 100)}%`
        : '—'
      doc.text(rate, sumCols.rate, y, { align: 'right' })
      y += 16
    })

    // Totals row across all months
    if (reportData.length > 1) {
      const totIncome = reportData.reduce((s, m) => s + m.totalIncome, 0)
      const totSpent  = reportData.reduce((s, m) => s + m.totalSpent, 0)
      const totNet    = totIncome - totSpent
      const totRate   = totIncome > 0 ? `${Math.round((1 - totSpent / totIncome) * 100)}%` : '—'
      y += 4
      hr(y, 0.4)
      y += 14
      doc.setFont('times', 'bold')
      doc.text('All periods',           sumCols.month, y)
      doc.text(formatCurrency(totIncome), sumCols.income, y, { align: 'right' })
      doc.text(formatCurrency(totSpent),  sumCols.spent,  y, { align: 'right' })
      doc.text((totNet >= 0 ? '+' : '') + formatCurrency(totNet), sumCols.net, y, { align: 'right' })
      doc.text(totRate, sumCols.rate, y, { align: 'right' })
      y += 16
    }

    y += 18
    hr(y, 1)
    y += 22

    // ─── Per-month detail sections ────────────────────────
    for (const m of reportData) {
      // Reserve enough space to put a month header + first few rows
      // on the same page; if not enough, push to next page.
      y = newPageIfNeeded(y, 100)

      // Month header
      setBlack()
      doc.setFont('times', 'bold'); doc.setFontSize(13)
      doc.text(`${m.monthName} ${m.year}`, margin, y)
      y += 16
      setDim()
      doc.setFont('times', 'normal'); doc.setFontSize(10)
      doc.text(`${m.txCount} transactions · ${formatCurrency(m.totalSpent)} total`, margin, y)
      y += 14

      // Detail table column layout
      const col = {
        cat:    margin,
        budget: margin + contentW * 0.50,
        spent:  margin + contentW * 0.72,
        var:    pageW - margin,
      }

      // Header row
      setDim()
      doc.setFont('times', 'normal'); doc.setFontSize(9)
      doc.text('Category', col.cat, y)
      if (m.hasBudget) {
        doc.text('Budgeted', col.budget, y, { align: 'right' })
        doc.text('Spent',    col.spent,  y, { align: 'right' })
        doc.text('Variance', col.var,    y, { align: 'right' })
      } else {
        doc.text('Spent', col.var, y, { align: 'right' })
      }
      y += 4
      hr(y, 0.3)
      y += 12

      // Filter to non-zero rows; sort by spent desc so biggest items first
      const visible = m.catData
      .filter(c => c.spent > 0)
        .sort((a, b) => b.spent - a.spent)

      setBlack()
      doc.setFont('times', 'normal'); doc.setFontSize(10)

      for (const cat of visible) {
        y = newPageIfNeeded(y, 16)
        // Strip any emoji from name for clean ink output
        const cleanName = (cat.name || '').replace(/^[\p{Extended_Pictographic}\s]+/u, '').trim() || cat.name
        doc.setFont('times', 'bold')
        doc.text(cleanName, col.cat, y)
        if (m.hasBudget) {
          doc.setFont('times', 'normal')
          doc.text(cat.limit > 0 ? formatCurrency(cat.limit) : '—', col.budget, y, { align: 'right' })
          doc.setFont('times', 'bold')
          doc.text(formatCurrency(cat.spent), col.spent, y, { align: 'right' })
          if (cat.limit > 0) {
            const variance = cat.limit - cat.spent
            doc.setFont('times', 'normal')
            doc.text(
              (variance >= 0 ? '+' : '') + formatCurrency(variance),
              col.var, y, { align: 'right' }
            )
          } else {
            doc.text('—', col.var, y, { align: 'right' })
          }
        } else {
          doc.setFont('times', 'bold')
          doc.text(formatCurrency(cat.spent), col.var, y, { align: 'right' })
        }
        y += 14

        // Subcategories: only show if they have spending or a limit
        const visibleChildren = cat.children.filter(c => c.spent > 0)
        for (const ch of visibleChildren) {
          y = newPageIfNeeded(y, 14)
          const cleanCh = (ch.name || '').replace(/^[\p{Extended_Pictographic}\s]+/u, '').trim() || ch.name
          setDim()
          doc.setFont('times', 'normal'); doc.setFontSize(9.5)
          doc.text(`   ${cleanCh}`, col.cat, y)
          if (m.hasBudget) {
            doc.text(ch.limit > 0 ? formatCurrency(ch.limit) : '—', col.budget, y, { align: 'right' })
            doc.text(formatCurrency(ch.spent), col.spent, y, { align: 'right' })
            if (ch.limit > 0) {
              const variance = ch.limit - ch.spent
              doc.text(
                (variance >= 0 ? '+' : '') + formatCurrency(variance),
                col.var, y, { align: 'right' }
              )
            } else {
              doc.text('—', col.var, y, { align: 'right' })
            }
          } else {
            doc.text(formatCurrency(ch.spent), col.var, y, { align: 'right' })
          }
          y += 12
          setBlack()
          doc.setFont('times', 'normal'); doc.setFontSize(10)
        }
      }

      // Total row
      y = newPageIfNeeded(y, 24)
      y += 4
      hr(y, 0.4)
      y += 14
      setBlack()
      doc.setFont('times', 'bold'); doc.setFontSize(11)
      doc.text('Total', col.cat, y)
      if (m.hasBudget) {
        const totalBudget = m.catData.reduce((s, c) => s + c.limit, 0)
        const totalVar = totalBudget - m.totalSpent
        doc.text(formatCurrency(totalBudget), col.budget, y, { align: 'right' })
        doc.text(formatCurrency(m.totalSpent), col.spent, y, { align: 'right' })
        doc.text((totalVar >= 0 ? '+' : '') + formatCurrency(totalVar), col.var, y, { align: 'right' })
      } else {
        doc.text(formatCurrency(m.totalSpent), col.var, y, { align: 'right' })
      }
      y += 22

      // Income detail (if any)
      if (m.income.length > 0) {
        y = newPageIfNeeded(y, 50)
        setDim()
        doc.setFont('times', 'normal'); doc.setFontSize(9)
        doc.text('INCOME', margin, y)
        y += 4
        hr(y, 0.3)
        y += 12

        setBlack()
        doc.setFont('times', 'normal'); doc.setFontSize(10)
        for (const src of m.income) {
          y = newPageIfNeeded(y, 14)
          doc.text(src.name, col.cat, y)
          doc.text(formatCurrency(src.monthly), col.var, y, { align: 'right' })
          y += 13
        }
        y += 4
        hr(y, 0.4)
        y += 14
        doc.setFont('times', 'bold'); doc.setFontSize(11)
        doc.text('Total income', col.cat, y)
        doc.text(formatCurrency(m.totalIncome), col.var, y, { align: 'right' })
        y += 22
      }

      // Net summary line
      y = newPageIfNeeded(y, 30)
      doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.4)
      doc.line(margin, y - 4, pageW - margin, y - 4)
      doc.setFont('times', 'bold'); doc.setFontSize(11)
      const netLabel = m.net >= 0 ? 'Net (saved)' : 'Net (overspent)'
      doc.text(netLabel, col.cat, y + 8)
      doc.text((m.net >= 0 ? '+' : '') + formatCurrency(m.net), col.var, y + 8, { align: 'right' })
      y += 30
    }

    addFooter(doc.internal.getNumberOfPages())
    return doc
  }

  // Generate the PDF, open it in a new tab for preview
  const handlePreviewFinancialPDF = async () => {
    if (!reportData.length) return
    const doc = await handleDownloadFinancialPDF()
    if (doc) {
      const blobUrl = doc.output('bloburl')
      window.open(blobUrl, '_blank')
    }
  }

  // Generate the PDF and trigger a download
  const handleSaveFinancialPDF = async () => {
    if (!reportData.length) return
    const doc = await handleDownloadFinancialPDF()
    if (doc) {
      doc.save(`merq-financial-report-${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}.pdf`)
    }
  }

  // ─── Business expense PDF — same clean aesthetic ──────────
  const handleGenerateExpenseReport = async () => {
    const bizTxs = getBusinessTransactionsForPeriod()
    if (bizTxs.length === 0) {
      alert(`No business expenses found for ${MONTH_NAMES[expenseReportMonth - 1]} ${expenseReportYear}.`)
      return
    }
    setGeneratingExpenseReport(true)

    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' })
    const pageW = doc.internal.pageSize.getWidth()
    const pageH = doc.internal.pageSize.getHeight()
    const margin = 56
    const contentW = pageW - margin * 2

    const setBlack = () => doc.setTextColor(0, 0, 0)
    const setDim   = () => doc.setTextColor(110, 110, 110)
    const setQuiet = () => doc.setTextColor(160, 160, 160)
    const hr = (y, weight = 0.5) => {
      doc.setDrawColor(0, 0, 0); doc.setLineWidth(weight)
      doc.line(margin, y, pageW - margin, y)
    }
    const addFooter = (pageNum) => {
      setQuiet()
      doc.setFont('times', 'italic'); doc.setFontSize(8)
      doc.text('Merq Vault — getmerq.com', margin, pageH - 30)
      doc.text(`Page ${pageNum}`, pageW - margin, pageH - 30, { align: 'right' })
    }

    let y = margin
    const dateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    const total = bizTxs.reduce((sum, tx) => sum + parseFloat(tx.amount), 0)

    // Title
    setBlack()
    doc.setFont('times', 'bold'); doc.setFontSize(20)
    doc.text('Business Expenses', margin, y)
    y += 24

    setDim()
    doc.setFont('times', 'normal'); doc.setFontSize(11)
    doc.text(`${MONTH_NAMES[expenseReportMonth - 1]} ${expenseReportYear}`, margin, y)
    y += 14
    doc.text(`Prepared ${dateStr}`, margin, y)
    y += 22

    hr(y, 1)
    y += 18

    // Summary line
    setBlack()
    doc.setFont('times', 'normal'); doc.setFontSize(11)
    doc.text(`${bizTxs.length} transaction${bizTxs.length === 1 ? '' : 's'}`, margin, y)
    doc.setFont('times', 'bold'); doc.setFontSize(14)
    doc.text(`Total: ${formatCurrency(total)}`, pageW - margin, y, { align: 'right' })
    y += 20
    hr(y, 0.4)
    y += 18

    // Table headers
    const cols = {
      date:     margin,
      merchant: margin + contentW * 0.13,
      category: margin + contentW * 0.45,
      memo:     margin + contentW * 0.65,
      amount:   pageW - margin,
    }

    setDim()
    doc.setFont('times', 'normal'); doc.setFontSize(9)
    doc.text('Date',     cols.date,     y)
    doc.text('Merchant', cols.merchant, y)
    doc.text('Category', cols.category, y)
    doc.text('Memo',     cols.memo,     y)
    doc.text('Amount',   cols.amount,   y, { align: 'right' })
    y += 6
    hr(y, 0.3)
    y += 14

    // Rows
    setBlack()
    doc.setFont('times', 'normal'); doc.setFontSize(10)
    bizTxs.forEach(tx => {
      if (y > pageH - 90) {
        addFooter(doc.internal.getNumberOfPages())
        doc.addPage()
        y = margin
        // Re-draw header
        setDim()
        doc.setFont('times', 'normal'); doc.setFontSize(9)
        doc.text('Date',     cols.date,     y)
        doc.text('Merchant', cols.merchant, y)
        doc.text('Category', cols.category, y)
        doc.text('Memo',     cols.memo,     y, )
        doc.text('Amount',   cols.amount,   y, { align: 'right' })
        y += 6
        hr(y, 0.3)
        y += 14
        setBlack()
        doc.setFont('times', 'normal'); doc.setFontSize(10)
      }
      const dateLabel = new Date(tx.transaction_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      doc.text(dateLabel, cols.date, y)
      doc.text((tx.merchant_name || 'Unknown').substring(0, 26), cols.merchant, y)
      const cleanCat = (tx.budget_categories?.name || '').replace(/^[\p{Extended_Pictographic}\s]+/u, '').trim() || (tx.budget_categories?.name || '')
      setDim()
      doc.text(cleanCat.substring(0, 18), cols.category, y)
      doc.text((tx.business_memo || tx.note || '').substring(0, 22), cols.memo, y)
      setBlack()
      doc.setFont('times', 'bold')
      doc.text(formatCurrency(parseFloat(tx.amount)), cols.amount, y, { align: 'right' })
      doc.setFont('times', 'normal')
      y += 14
    })

    y += 4
    hr(y, 0.4)
    y += 14
    doc.setFont('times', 'bold'); doc.setFontSize(11)
    doc.text('Total', cols.date, y)
    doc.text(formatCurrency(total), cols.amount, y, { align: 'right' })
    y += 20

    // Receipts as separate pages
    const txsWithReceipts = bizTxs.filter(tx => tx.receipt_url)
    if (txsWithReceipts.length > 0) {
      addFooter(doc.internal.getNumberOfPages())
      doc.addPage()
      y = margin

      setBlack()
      doc.setFont('times', 'bold'); doc.setFontSize(16)
      doc.text('Receipt Images', margin, y)
      y += 20

      setDim()
      doc.setFont('times', 'normal'); doc.setFontSize(10)
      doc.text(
        `${MONTH_NAMES[expenseReportMonth - 1]} ${expenseReportYear} · ${txsWithReceipts.length} receipt${txsWithReceipts.length === 1 ? '' : 's'}`,
        margin, y
      )
      y += 22
      hr(y, 1)
      y += 18

      for (const tx of txsWithReceipts) {
        const imgData = await loadImageAsBase64(tx.receipt_url)
        const labelH = 32
        const maxImgH = 360

        if (y + labelH + maxImgH + 20 > pageH - margin) {
          addFooter(doc.internal.getNumberOfPages())
          doc.addPage()
          y = margin
        }

        // Receipt label
        const txDate = new Date(tx.transaction_date + 'T12:00:00')
        setBlack()
        doc.setFont('times', 'bold'); doc.setFontSize(11)
        doc.text(tx.merchant_name || 'Receipt', margin, y)
        doc.text(formatCurrency(parseFloat(tx.amount)), pageW - margin, y, { align: 'right' })
        y += 12
        setDim()
        doc.setFont('times', 'normal'); doc.setFontSize(9)
        doc.text(
          txDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
          margin, y
        )
        y += 12

        if (imgData) {
          try {
            const imgProps = doc.getImageProperties(imgData)
            const ratio = imgProps.width / imgProps.height
            let imgW = contentW, imgH = imgW / ratio
            if (imgH > maxImgH) { imgH = maxImgH; imgW = imgH * ratio }
            doc.addImage(imgData, 'JPEG', margin + (contentW - imgW) / 2, y, imgW, imgH)
            y += imgH + 24
          } catch (e) {
            setQuiet()
            doc.setFont('times', 'italic'); doc.setFontSize(10)
            doc.text('Receipt image could not be displayed', pageW / 2, y + 20, { align: 'center' })
            y += 50
          }
        } else {
          setQuiet()
          doc.setFont('times', 'italic'); doc.setFontSize(10)
          doc.text('Receipt image could not be loaded', pageW / 2, y + 20, { align: 'center' })
          y += 50
        }
      }
    }

    addFooter(doc.internal.getNumberOfPages())
    setGeneratingExpenseReport(false)
    return doc
  }

  const handlePreviewExpenseReport = async () => {
    const doc = await handleGenerateExpenseReport()
    if (doc) {
      const blobUrl = doc.output('bloburl')
      window.open(blobUrl, '_blank')
    }
  }

  const handleSaveExpenseReport = async () => {
    const doc = await handleGenerateExpenseReport()
    if (doc) {
      doc.save(`merq-expense-report-${expenseReportYear}-${String(expenseReportMonth).padStart(2, '0')}.pdf`)
    }
  }

  const bizTxsPreview = getBusinessTransactionsForPeriod()
  const partnerName = partnerProfile?.full_name?.split(' ')[0]
  const reportPeople = partnerName ? `${partnerName} & Philip` : 'Personal'
  const reportMonths = reportData.map(m => `${m.monthName} ${m.year}`).join(' · ')

  // ───────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={styles.loading}>
        <span>Loading reports…</span>
      </div>
    )
  }

  const sharingFirst = partnerProfile ? firstName(partnerProfile.full_name) : null
  const todayMonth = now.getMonth() + 1
  const todayYear = now.getFullYear()
  const currentMonthBudget = availableMonths.find(b => b.month === todayMonth && b.year === todayYear)

  return (
    <div>
      {/* Print stylesheet — strip the chrome so only the print layout shows */}
      <style>{`
        @media screen { .print-only { display: none !important; } }
        @media print {
          @page { margin: 0.5in; size: letter portrait; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          /* Hide everything in the AppShell except the print-only block */
          .app-shell > * { display: none !important; }
          .app-shell .screen-only { display: none !important; }
          /* Show the print-only block, fixed to top of page */
          .print-only {
            display: block !important;
            position: fixed !important;
            inset: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            overflow: visible !important;
            z-index: 9999 !important;
          }
          .print-only * { visibility: visible !important; }
          html, body, #root, .app-shell {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            display: block !important;
            overflow: visible !important;
          }
        }
      `}</style>

      <div className="screen-only" style={isMobile ? styles.pageMobile : styles.page}>

        {/* Header */}
        <div style={isMobile ? styles.headerMobile : styles.header}>
          <div style={styles.headerLeft}>
            <h1 style={styles.pageTitle}>Reports</h1>
            <div style={atoms.pageMeta}>
              <span>Financial summaries and tax exports</span>
              {sharingFirst && (
                <>
                  <span style={atoms.pageMetaDot} />
                  <span style={atoms.sharingPill}>
                    Sharing with {sharingFirst}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Quick action — Preview this month's review */}
        {currentMonthBudget && (
          <div style={styles.quickCard}>
            <div style={styles.quickCardLeft}>
              <div>
                <div style={styles.quickTitle}>This month's review</div>
                <div style={styles.quickSub}>
                  A printable PDF snapshot of {MONTH_NAMES[todayMonth - 1]} {todayYear} — totals, categories, income.
                </div>
              </div>
            </div>
            <button
              type="button"
              style={styles.quickCta}
              onClick={async () => {
                setSelectedMonths([currentMonthBudget.id])
                await new Promise(r => setTimeout(r, 50))
                await generateReport()
                await new Promise(r => setTimeout(r, 100))
                await handlePreviewFinancialPDF()
              }}
            >
              Preview review
            </button>
          </div>
        )}

        {/* Financial Summary Report card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.cardTitle}>Financial summary</h2>
              <p style={styles.cardSub}>
                Pick months to compare. Generates an on-screen review and a downloadable PDF
                with category breakdowns, budget variance, and savings rate.
              </p>
            </div>
          </div>

          <div style={styles.cardBody}>
            <span style={styles.fieldLabel}>Months</span>
            <div style={styles.monthChipRow}>
              {availableMonths.length === 0 && (
                <span style={styles.emptyChipsHint}>
                  No months yet — set up a plan first to start generating reports.
                </span>
              )}
              {availableMonths.map(b => {
                const active = selectedMonths.includes(b.id)
                return (
                  <button
                    key={b.id}
                    type="button"
                    className="btn-compact"
                    style={{
                      ...styles.monthChip,
                      ...(active ? styles.monthChipActive : {}),
                    }}
                    onClick={() => toggleMonth(b.id)}
                  >
                    {SHORT_MONTHS[b.month - 1]} {b.year}
                    {active && <span aria-hidden="true" style={{ color: theme.colors.green }}>✓</span>}
                  </button>
                )
              })}
            </div>

            <div style={styles.cardActions}>
              <button
                type="button"
                style={{
                  ...styles.primaryBtn,
                  opacity: (generating || !selectedMonths.length) ? 0.5 : 1,
                  cursor: (generating || !selectedMonths.length) ? 'not-allowed' : 'pointer',
                }}
                onClick={generateReport}
                disabled={generating || !selectedMonths.length}
              >
                {generating ? 'Generating…' : 'Generate report'}
              </button>
              {reportData.length > 0 && (
                <>
                  <button
                    type="button"
                    style={styles.secondaryBtn}
                    onClick={handlePreviewFinancialPDF}
                  >
                        Preview PDF
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryBtn}
                    onClick={handleSaveFinancialPDF}
                  >
                        Download PDF
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Business Expense Report card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.cardTitle}>Business expense report</h2>
              <p style={styles.cardSub}>
                A PDF of business-tagged transactions for a single month, with receipt images attached.
                Built for accountants and tax records.
              </p>
            </div>
          </div>

          <div style={styles.cardBody}>
            <div style={styles.bizControls}>
              <div style={styles.bizField}>
                <span style={styles.fieldLabel}>Month</span>
                <div style={styles.selectWrap}>
                  <select
                    value={expenseReportMonth}
                    onChange={e => setExpenseReportMonth(Number(e.target.value))}
                    style={styles.select}
                  >
                    {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                  <span style={{ ...styles.selectChevron, fontSize: '11px', lineHeight: 1 }} aria-hidden="true">▾</span>
                </div>
              </div>
              <div style={styles.bizField}>
                <span style={styles.fieldLabel}>Year</span>
                <div style={styles.selectWrap}>
                  <select
                    value={expenseReportYear}
                    onChange={e => setExpenseReportYear(Number(e.target.value))}
                    style={styles.select}
                  >
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <span style={{ ...styles.selectChevron, fontSize: '11px', lineHeight: 1 }} aria-hidden="true">▾</span>
                </div>
              </div>
            </div>

            <div style={styles.bizStats}>
              <div style={styles.bizStat}>
                <div style={styles.bizStatNum}>{bizTxsPreview.length}</div>
                <div style={styles.bizStatLbl}>expenses</div>
              </div>
              <div style={styles.bizStatDivider} />
              <div style={styles.bizStat}>
                <div style={styles.bizStatNum}>{bizTxsPreview.filter(tx => tx.receipt_url).length}</div>
                <div style={styles.bizStatLbl}>receipts</div>
              </div>
              <div style={styles.bizStatDivider} />
              <div style={styles.bizStat}>
                <div style={{ ...styles.bizStatNum, color: theme.colors.green }}>
                  {formatCurrency(bizTxsPreview.reduce((s, tx) => s + parseFloat(tx.amount), 0))}
                </div>
                <div style={styles.bizStatLbl}>total</div>
              </div>
            </div>

            <div style={styles.cardActions}>
              <button
                type="button"
                onClick={handlePreviewExpenseReport}
                disabled={generatingExpenseReport || bizTxsPreview.length === 0}
                style={{
                  ...styles.secondaryBtn,
                  opacity: (generatingExpenseReport || bizTxsPreview.length === 0) ? 0.5 : 1,
                  cursor: (generatingExpenseReport || bizTxsPreview.length === 0) ? 'not-allowed' : 'pointer',
                }}
              >
                Preview PDF
              </button>
              <button
                type="button"
                onClick={handleSaveExpenseReport}
                disabled={generatingExpenseReport || bizTxsPreview.length === 0}
                style={{
                  ...styles.primaryBtn,
                  opacity: (generatingExpenseReport || bizTxsPreview.length === 0) ? 0.5 : 1,
                  cursor: (generatingExpenseReport || bizTxsPreview.length === 0) ? 'not-allowed' : 'pointer',
                }}
              >
                {generatingExpenseReport ? 'Generating…' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>

        {/* Generated report output (on-screen preview) */}
        {reportData.length > 0 && (
          <div style={styles.previewCard}>
            <div style={styles.previewHeader}>
              <div>
                <div style={styles.previewKicker}>Financial Summary</div>
                <div style={styles.previewMonths}>{reportMonths}</div>
                <div style={styles.previewMeta}>
                  Prepared for {reportPeople} · {now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
            </div>

            <div style={styles.summaryRow}>
              {reportData.map((m, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.summaryCol,
                    borderRight: i < reportData.length - 1
                      ? `1px solid ${theme.colors.line}` : 'none',
                  }}
                >
                  <div style={styles.summaryColLabel}>
                    {m.monthName} {m.year}
                  </div>
                  <SummaryStat label="Income" value={formatCurrency(m.totalIncome)} color={theme.colors.green} />
                  <SummaryStat label="Spent" value={formatCurrency(m.totalSpent)} color={theme.colors.plum} />
                  <SummaryStat
                    label="Net"
                    value={(m.net >= 0 ? '+' : '') + formatCurrency(m.net)}
                    color={m.net >= 0 ? theme.colors.green : theme.colors.rose}
                  />
                  {m.totalIncome > 0 && (
                    <div style={styles.savingsRate}>
                      {Math.round((1 - m.totalSpent / m.totalIncome) * 100)}% savings rate
                    </div>
                  )}
                </div>
              ))}
            </div>

            {reportData.map((m, mi) => (
              <MonthSection key={mi} m={m} />
            ))}
          </div>
        )}

        {reportData.length === 0 && !generating && (
          <div style={styles.emptyHint}>
            <div style={styles.emptyTitle}>No report generated yet</div>
            <div style={styles.emptyText}>
              Pick months above and click <strong>Generate report</strong> to see a breakdown.
            </div>
          </div>
        )}
      </div>

      {/* PRINT LAYOUT — clean serif, ink-friendly */}
      <PrintLayout
        reportData={reportData}
        reportPeople={reportPeople}
        now={now}
      />
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────

function SummaryStat({ label, value, color }) {
  return (
    <div style={styles.summaryStat}>
      <div style={styles.summaryStatLabel}>{label}</div>
      <div style={{ ...styles.summaryStatValue, color }}>{value}</div>
    </div>
  )
}

// Strip leading emoji from a category name for clean ink output
function cleanName(s) {
  if (!s) return ''
  return s.replace(/^[\p{Extended_Pictographic}\s]+/u, '').trim() || s
}

function MonthSection({ m }) {
  const { formatCurrency } = useCurrency()
  const visible = m.catData
    .filter(c => c.spent > 0)
    .sort((a, b) => b.spent - a.spent)
  const totalBudget = m.catData.reduce((s, c) => s + c.limit, 0)

  return (
    <div style={styles.monthSection}>
      <div style={styles.monthSectionHead}>
        <div style={styles.monthSectionName}>{m.monthName} {m.year}</div>
        <div style={styles.monthSectionMeta}>
          {m.txCount} transactions · {formatCurrency(m.totalSpent)} total
        </div>
      </div>

      <table style={styles.catTable}>
        <thead>
          <tr style={styles.catTableHead}>
            <th style={styles.thLeft}>Category</th>
            {m.hasBudget && <th style={styles.thRight}>Budgeted</th>}
            <th style={styles.thRight}>Spent</th>
            {m.hasBudget && <th style={styles.thRight}>Variance</th>}
          </tr>
        </thead>
        <tbody>
          {visible.map((cat, ci) => (
            <React.Fragment key={ci}>
              <tr style={styles.catRow}>
                <td style={styles.tdLeft}>
                  <strong style={styles.catName}>{cleanName(cat.name)}</strong>
                </td>
                {m.hasBudget && <td style={styles.tdRightMuted}>
                  {cat.limit > 0 ? formatCurrency(cat.limit) : '—'}
                </td>}
                <td style={{
                  ...styles.tdRightStrong,
                  color: cat.limit > 0 && cat.spent > cat.limit
                    ? theme.colors.rose : theme.colors.plum,
                }}>
                  {formatCurrency(cat.spent)}
                </td>
                {m.hasBudget && <td style={styles.tdRight}>
                  {cat.limit > 0 ? (
                    <span style={{
                      color: cat.spent <= cat.limit ? theme.colors.green : theme.colors.rose,
                      fontWeight: 600,
                    }}>
                      {cat.spent <= cat.limit ? '+' : ''}{formatCurrency(cat.limit - cat.spent)}
                    </span>
                  ) : '—'}
                </td>}
              </tr>
              {cat.children.filter(c => c.spent > 0).map((ch, chi) => (
                <tr key={chi} style={styles.subcatRow}>
                  <td style={styles.subcatTdLeft}>
                    {cleanName(ch.name)}
                  </td>
                  {m.hasBudget && <td style={styles.subcatTdRightMuted}>
                    {ch.limit > 0 ? formatCurrency(ch.limit) : '—'}
                  </td>}
                  <td style={{
                    ...styles.subcatTdRight,
                    color: ch.limit > 0 && ch.spent > ch.limit
                      ? theme.colors.rose : theme.colors.plumSoft,
                  }}>
                    {formatCurrency(ch.spent)}
                  </td>
                  {m.hasBudget && <td style={styles.subcatTdRight}>
                    {ch.limit > 0 ? (
                      <span style={{ color: ch.spent <= ch.limit ? theme.colors.green : theme.colors.rose }}>
                        {ch.spent <= ch.limit ? '+' : ''}{formatCurrency(ch.limit - ch.spent)}
                      </span>
                    ) : '—'}
                  </td>}
                </tr>
              ))}
            </React.Fragment>
          ))}
          <tr style={styles.totalRow}>
            <td style={styles.totalTd}>Total</td>
            {m.hasBudget && <td style={{ ...styles.totalTd, textAlign: 'right' }}>{formatCurrency(totalBudget)}</td>}
            <td style={{ ...styles.totalTd, textAlign: 'right' }}>{formatCurrency(m.totalSpent)}</td>
            {m.hasBudget && <td style={{
              ...styles.totalTd,
              textAlign: 'right',
              color: (totalBudget - m.totalSpent) >= 0 ? theme.colors.green : theme.colors.rose,
            }}>
              {(totalBudget - m.totalSpent) >= 0 ? '+' : ''}{formatCurrency(totalBudget - m.totalSpent)}
            </td>}
          </tr>
        </tbody>
      </table>

      {m.income.length > 0 && (
        <div style={styles.incomeRow}>
          <div style={styles.incomeRowLabel}>Income</div>
          <div style={styles.incomeRowItems}>
            {m.income.map((src, si) => (
              <span key={si} style={styles.incomeItem}>
                <span style={{ color: theme.colors.inkMuted }}>{src.name}</span>{' '}
                <strong style={{ color: theme.colors.plum, fontWeight: 600 }}>
                  {formatCurrency(src.monthly)}
                </strong>
              </span>
            ))}
            <span style={styles.incomeTotal}>
              <span style={styles.incomeTotalLabel}>Total</span>
              <span style={styles.incomeTotalValue}>{formatCurrency(m.totalIncome)}</span>
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Print layout — ink-friendly, serif, black on white ────
function PrintLayout({ reportData, reportPeople, now }) {
  const { formatCurrency } = useCurrency()
  if (reportData.length === 0) return null
  const reportMonthsStr = reportData.map(m => `${m.monthName} ${m.year}`).join(', ')
  const dateStr = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  const totIncome = reportData.reduce((s, m) => s + m.totalIncome, 0)
  const totSpent  = reportData.reduce((s, m) => s + m.totalSpent, 0)
  const totNet    = totIncome - totSpent

  return (
    <div className="print-only" style={printStyles.page}>
      {/* Title block */}
      <div style={printStyles.title}>Financial Summary</div>
      <div style={printStyles.subtitle}>{reportMonthsStr}</div>
      <div style={printStyles.meta}>Prepared for {reportPeople} · {dateStr}</div>
      <hr style={printStyles.hr} />

      {/* Summary table */}
      <div style={printStyles.sectionLabel}>SUMMARY</div>
      <table style={printStyles.sumTable}>
        <thead>
          <tr>
            <th style={printStyles.sumThLeft}>Period</th>
            <th style={printStyles.sumTh}>Income</th>
            <th style={printStyles.sumTh}>Expenses</th>
            <th style={printStyles.sumTh}>Net</th>
            <th style={printStyles.sumTh}>Savings</th>
          </tr>
        </thead>
        <tbody>
          {reportData.map((m, i) => (
            <tr key={i}>
              <td style={printStyles.sumTdLeft}>{m.monthName} {m.year}</td>
              <td style={printStyles.sumTd}>{formatCurrency(m.totalIncome)}</td>
              <td style={printStyles.sumTd}>{formatCurrency(m.totalSpent)}</td>
              <td style={printStyles.sumTd}>{(m.net >= 0 ? '+' : '') + formatCurrency(m.net)}</td>
              <td style={printStyles.sumTd}>
                {m.totalIncome > 0 ? `${Math.round((1 - m.totalSpent / m.totalIncome) * 100)}%` : '—'}
              </td>
            </tr>
          ))}
          {reportData.length > 1 && (
            <tr style={printStyles.sumTotalRow}>
              <td style={printStyles.sumTdLeftBold}>All periods</td>
              <td style={printStyles.sumTdBold}>{formatCurrency(totIncome)}</td>
              <td style={printStyles.sumTdBold}>{formatCurrency(totSpent)}</td>
              <td style={printStyles.sumTdBold}>{(totNet >= 0 ? '+' : '') + formatCurrency(totNet)}</td>
              <td style={printStyles.sumTdBold}>
                {totIncome > 0 ? `${Math.round((1 - totSpent / totIncome) * 100)}%` : '—'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Per-month detail */}
      {reportData.map((m, mi) => {
        const visible = m.catData
          .filter(c => c.spent > 0)
          .sort((a, b) => b.spent - a.spent)
        const totalBudget = m.catData.reduce((s, c) => s + c.limit, 0)

        return (
          <div key={mi} style={printStyles.monthBlock}>
            <div style={printStyles.monthHeader}>
              {m.monthName} {m.year}
            </div>
            <div style={printStyles.monthMeta}>
              {m.txCount} transactions · {formatCurrency(m.totalSpent)} total
            </div>

            <table style={printStyles.detailTable}>
              <thead>
                <tr>
                  <th style={printStyles.detailThLeft}>Category</th>
                  {m.hasBudget && <th style={printStyles.detailTh}>Budgeted</th>}
                  <th style={printStyles.detailTh}>Spent</th>
                  {m.hasBudget && <th style={printStyles.detailTh}>Variance</th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((cat, ci) => (
                  <React.Fragment key={ci}>
                    <tr>
                      <td style={printStyles.detailTdLeft}>
                        <strong>{cleanName(cat.name)}</strong>
                      </td>
                      {m.hasBudget && <td style={printStyles.detailTd}>
                        {cat.limit > 0 ? formatCurrency(cat.limit) : '—'}
                      </td>}
                      <td style={printStyles.detailTdBold}>{formatCurrency(cat.spent)}</td>
                      {m.hasBudget && <td style={printStyles.detailTd}>
                        {cat.limit > 0
                          ? (cat.spent <= cat.limit ? '+' : '') + formatCurrency(cat.limit - cat.spent)
                          : '—'}
                      </td>}
                    </tr>
                    {cat.children.filter(c => c.spent > 0).map((ch, chi) => (
                      <tr key={chi}>
                        <td style={printStyles.detailSubTdLeft}>{cleanName(ch.name)}</td>
                        {m.hasBudget && <td style={printStyles.detailSubTd}>
                          {ch.limit > 0 ? formatCurrency(ch.limit) : '—'}
                        </td>}
                        <td style={printStyles.detailSubTd}>{formatCurrency(ch.spent)}</td>
                        {m.hasBudget && <td style={printStyles.detailSubTd}>
                          {ch.limit > 0
                            ? (ch.spent <= ch.limit ? '+' : '') + formatCurrency(ch.limit - ch.spent)
                            : '—'}
                        </td>}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                <tr style={printStyles.totalRow}>
                  <td style={printStyles.totalTd}><strong>Total</strong></td>
                  {m.hasBudget && <td style={printStyles.totalTd}><strong>{formatCurrency(totalBudget)}</strong></td>}
                  <td style={printStyles.totalTd}><strong>{formatCurrency(m.totalSpent)}</strong></td>
                  {m.hasBudget && <td style={printStyles.totalTd}><strong>
                    {((totalBudget - m.totalSpent) >= 0 ? '+' : '') + formatCurrency(totalBudget - m.totalSpent)}
                  </strong></td>}
                </tr>
              </tbody>
            </table>

            {m.income.length > 0 && (
              <>
                <div style={printStyles.incomeLabel}>INCOME</div>
                <table style={printStyles.detailTable}>
                  <tbody>
                    {m.income.map((src, si) => (
                      <tr key={si}>
                        <td style={printStyles.detailTdLeft}>{src.name}</td>
                        <td style={printStyles.detailTd}>{formatCurrency(src.monthly)}</td>
                      </tr>
                    ))}
                    <tr style={printStyles.totalRow}>
                      <td style={printStyles.totalTd}><strong>Total income</strong></td>
                      <td style={printStyles.totalTd}><strong>{formatCurrency(m.totalIncome)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            <div style={printStyles.netLine}>
              <strong>{m.net >= 0 ? 'Net (saved)' : 'Net (overspent)'}</strong>
              <strong>{(m.net >= 0 ? '+' : '') + formatCurrency(m.net)}</strong>
            </div>
          </div>
        )
      })}

      <div style={printStyles.footer}>
        Merq Vault — getmerq.com · Observations only — not financial advice
      </div>
    </div>
  )
}

// ─── Styles ─────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts

const styles = {
  page: {
    maxWidth: '1100px', margin: '0 auto', width: '100%',
    padding: '24px 32px 120px',
    fontFamily: f.body, color: c.plum,
  },
  pageMobile: {
    width: '100%', paddingBottom: '120px',
    padding: '0 16px 80px',
    fontFamily: f.body, color: c.plum,
  },
  loading: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '10px', padding: '60px', color: c.inkMuted, fontSize: '14px',
  },

  header: {
    display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
    marginBottom: '20px', gap: '14px', flexWrap: 'wrap',
  },
  headerMobile: {
    padding: '14px 0 14px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  pageTitle: {
    fontFamily: f.display, fontWeight: 600, fontSize: '32px',
    letterSpacing: '-0.025em', color: c.plum, margin: 0,
  },

  // Quick action card
  quickCard: {
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '14px',
    padding: '16px 18px',
    marginBottom: '20px',
    boxShadow: theme.shadows.card,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    flexWrap: 'wrap',
  },
  quickCardLeft: {
    display: 'flex', alignItems: 'center', gap: '14px',
    minWidth: 0, flex: 1,
  },
  quickIcon: {
    width: '40px', height: '40px',
    borderRadius: '10px',
    background: c.greenSoft, color: c.green,
    display: 'grid', placeItems: 'center', flexShrink: 0,
  },
  quickTitle: {
    fontSize: '15px', fontWeight: 600,
    color: c.plum, marginBottom: '2px',
  },
  quickSub: { fontSize: '12.5px', color: c.inkMuted },
  quickCta: {
    background: c.green, color: '#FFFFFF',
    border: 'none', padding: '10px 18px',
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    flexShrink: 0,
  },

  card: {
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '14px',
    padding: '20px 22px 22px',
    marginBottom: '20px',
    boxShadow: theme.shadows.card,
  },
  cardHeader: {
    display: 'flex', gap: '14px',
    marginBottom: '16px',
    alignItems: 'flex-start',
  },
  cardIcon: {
    width: '40px', height: '40px',
    borderRadius: '10px',
    background: c.purpleSoft, color: c.purple,
    display: 'grid', placeItems: 'center', flexShrink: 0,
  },
  cardTitle: {
    fontFamily: f.body,
    fontSize: '17px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.005em',
    margin: '0 0 4px 0',
  },
  cardSub: {
    fontSize: '13px', color: c.inkMuted,
    lineHeight: 1.5, margin: 0,
  },
  cardBody: { display: 'flex', flexDirection: 'column', gap: '14px' },
  fieldLabel: {
    fontSize: '11.5px', fontWeight: 600,
    color: c.inkMuted, letterSpacing: '0.06em',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: '8px',
  },
  monthChipRow: {
    display: 'flex', flexWrap: 'wrap', gap: '6px',
  },
  monthChip: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '6px 12px',
    background: c.cream2,
    border: `1px solid ${c.line}`,
    borderRadius: '999px',
    fontFamily: f.body, fontSize: '12.5px', fontWeight: 600,
    color: c.plumSoft, cursor: 'pointer',
    transition: 'all 120ms ease',
  },
  monthChipActive: {
    background: c.greenSoft, color: c.green,
    borderColor: 'rgba(22, 163, 74, 0.25)',
  },
  emptyChipsHint: {
    fontSize: '13px', color: c.inkMuted, fontStyle: 'italic',
  },
  cardActions: {
    display: 'flex', gap: '8px', flexWrap: 'wrap',
    marginTop: '4px',
  },
  primaryBtn: {
    background: c.plum, color: c.cream,
    border: 'none', padding: '11px 20px',
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '6px',
  },
  secondaryBtn: {
    background: 'transparent', color: c.plumSoft,
    border: `1px solid ${c.lineStrong}`,
    padding: '11px 18px',
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '13.5px', fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: '6px',
  },

  bizControls: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  bizField: { flex: 1, minWidth: '140px' },
  selectWrap: { position: 'relative' },
  select: {
    width: '100%',
    padding: '11px 32px 11px 14px',
    background: c.white,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '10px',
    fontFamily: f.body, fontSize: '14px',
    color: c.plum, outline: 'none',
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  },
  selectChevron: {
    position: 'absolute', right: '12px', top: '50%',
    transform: 'translateY(-50%)',
    color: c.inkQuiet, pointerEvents: 'none',
  },
  bizStats: {
    display: 'flex', alignItems: 'center', gap: '20px',
    background: c.cream2, borderRadius: '10px',
    padding: '14px 18px',
  },
  bizStat: { textAlign: 'center', flex: 1 },
  bizStatNum: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '22px', letterSpacing: '-0.02em',
    color: c.plum, lineHeight: 1.1,
  },
  bizStatLbl: {
    fontSize: '11px', color: c.inkMuted,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    marginTop: '2px',
  },
  bizStatDivider: { width: '1px', height: '28px', background: c.line },

  // Preview card
  previewCard: {
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '16px',
    overflow: 'hidden',
    marginTop: '24px',
    boxShadow: theme.shadows.card,
  },
  previewHeader: {
    background: c.plum, color: c.cream,
    padding: '24px 28px',
    display: 'flex', alignItems: 'flex-start',
    justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap',
  },
  previewKicker: {
    fontSize: '11px', fontWeight: 600,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: 'rgba(250, 247, 242, 0.6)',
    marginBottom: '4px',
  },
  previewMonths: {
    fontFamily: f.display, fontSize: '24px', fontWeight: 600,
    letterSpacing: '-0.02em', color: c.cream,
    marginBottom: '4px',
  },
  previewMeta: {
    fontSize: '12.5px', color: 'rgba(250, 247, 242, 0.65)',
  },

  summaryRow: {
    display: 'flex', borderBottom: `1px solid ${c.line}`,
    background: c.cream2,
  },
  summaryCol: {
    flex: 1, padding: '20px 18px',
    textAlign: 'center',
  },
  summaryColLabel: {
    fontSize: '11px', fontWeight: 700,
    color: c.plum, letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '12px',
  },
  summaryStat: { marginBottom: '8px' },
  summaryStatLabel: {
    fontSize: '10px', color: c.inkMuted,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: '3px',
  },
  summaryStatValue: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '17px', letterSpacing: '-0.015em',
  },
  savingsRate: {
    fontSize: '11.5px', color: c.inkMuted,
    fontStyle: 'italic', marginTop: '6px',
  },

  monthSection: {
    borderBottom: `1px solid ${c.line}`,
  },
  monthSectionHead: {
    padding: '18px 28px 14px',
    background: c.cream2,
    borderBottom: `1px solid ${c.line}`,
  },
  monthSectionName: {
    fontFamily: f.display, fontSize: '20px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.02em',
  },
  monthSectionMeta: {
    fontSize: '12.5px', color: c.inkMuted,
    marginTop: '2px',
  },
  catTable: {
    width: '100%', borderCollapse: 'collapse',
  },
  catTableHead: { borderBottom: `2px solid ${c.line}` },
  thLeft: {
    padding: '10px 18px', textAlign: 'left',
    fontSize: '10.5px', fontWeight: 700,
    color: c.inkMuted, letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  thRight: {
    padding: '10px 18px', textAlign: 'right',
    fontSize: '10.5px', fontWeight: 700,
    color: c.inkMuted, letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  catRow: {
    background: c.white,
    borderBottom: `1px solid ${c.line}`,
  },
  tdLeft: {
    padding: '10px 18px', fontSize: '13.5px', color: c.plum,
  },
  tdRight: {
    padding: '10px 18px', textAlign: 'right',
    fontSize: '13px',
  },
  tdRightMuted: {
    padding: '10px 18px', textAlign: 'right',
    fontSize: '13px', color: c.inkMuted,
  },
  tdRightStrong: {
    padding: '10px 18px', textAlign: 'right',
    fontFamily: f.display, fontWeight: 600, fontSize: '14px',
    letterSpacing: '-0.015em',
  },
  catName: { fontWeight: 600, color: c.plum },
  subcatRow: {
    background: c.cream2,
    borderBottom: `1px solid ${c.line}`,
  },
  subcatTdLeft: {
    padding: '7px 18px 7px 36px',
    fontSize: '12.5px', color: c.plumSoft,
  },
  subcatTdRight: {
    padding: '7px 18px', textAlign: 'right',
    fontSize: '12.5px', color: c.plumSoft,
  },
  subcatTdRightMuted: {
    padding: '7px 18px', textAlign: 'right',
    fontSize: '12.5px', color: c.inkMuted,
  },
  totalRow: {
    background: c.cream3,
    borderTop: `2px solid ${c.line}`,
  },
  totalTd: {
    padding: '11px 18px',
    fontSize: '13px', fontWeight: 700,
    color: c.plum,
  },
  incomeRow: {
    padding: '14px 28px',
    background: c.cream2,
    display: 'flex', alignItems: 'center', gap: '14px',
    flexWrap: 'wrap',
  },
  incomeRowLabel: {
    fontSize: '10.5px', fontWeight: 700,
    color: c.inkMuted, letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  incomeRowItems: {
    display: 'flex', alignItems: 'center', gap: '20px',
    flexWrap: 'wrap', flex: 1,
  },
  incomeItem: { fontSize: '12.5px' },
  incomeTotal: {
    marginLeft: 'auto',
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  incomeTotalLabel: {
    fontSize: '10.5px', fontWeight: 700,
    color: c.inkMuted, letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  incomeTotalValue: {
    fontFamily: f.display, fontWeight: 600,
    fontSize: '15px', color: c.green,
    letterSpacing: '-0.015em',
  },

  emptyHint: {
    background: c.white,
    border: `1px dashed ${c.lineStrong}`,
    borderRadius: '14px',
    padding: '40px 28px',
    textAlign: 'center',
    marginTop: '8px',
  },
  emptyTitle: {
    fontFamily: f.display, fontSize: '17px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.015em',
    marginBottom: '6px',
  },
  emptyText: {
    fontSize: '13.5px', color: c.inkMuted, lineHeight: 1.5,
  },
}

// ─── Print-only layout styles — black/white serif aesthetic ─

const printStyles = {
  page: {
    fontFamily: 'Times, "Times New Roman", serif',
    color: '#000',
    background: '#fff',
    padding: '0',
    fontSize: '11px',
    lineHeight: 1.4,
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    marginBottom: '6px',
  },
  subtitle: {
    fontSize: '13px',
    color: '#444',
    marginBottom: '4px',
  },
  meta: {
    fontSize: '11px',
    color: '#666',
    marginBottom: '12px',
  },
  hr: {
    border: 'none',
    borderTop: '1px solid #000',
    marginBottom: '14px',
  },

  sectionLabel: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    marginBottom: '6px',
  },

  sumTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '20px',
  },
  sumThLeft: {
    textAlign: 'left',
    padding: '6px 0 4px',
    borderBottom: '0.5px solid #000',
    fontSize: '10px',
    color: '#444',
    fontWeight: 400,
    fontStyle: 'italic',
  },
  sumTh: {
    textAlign: 'right',
    padding: '6px 0 4px',
    borderBottom: '0.5px solid #000',
    fontSize: '10px',
    color: '#444',
    fontWeight: 400,
    fontStyle: 'italic',
  },
  sumTdLeft: {
    textAlign: 'left',
    padding: '5px 0',
    fontSize: '11px',
  },
  sumTd: {
    textAlign: 'right',
    padding: '5px 0',
    fontSize: '11px',
  },
  sumTotalRow: {
    borderTop: '0.5px solid #000',
  },
  sumTdLeftBold: {
    textAlign: 'left',
    padding: '6px 0',
    fontSize: '11px',
    fontWeight: 700,
  },
  sumTdBold: {
    textAlign: 'right',
    padding: '6px 0',
    fontSize: '11px',
    fontWeight: 700,
  },

  monthBlock: {
    pageBreakInside: 'auto',
    breakInside: 'auto',
    marginBottom: '24px',
  },
  monthHeader: {
    fontSize: '14px',
    fontWeight: 700,
    marginBottom: '2px',
    pageBreakAfter: 'avoid',
    breakAfter: 'avoid',
  },
  monthMeta: {
    fontSize: '10px',
    color: '#666',
    marginBottom: '10px',
    pageBreakAfter: 'avoid',
    breakAfter: 'avoid',
  },

  detailTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '12px',
  },
  detailThLeft: {
    textAlign: 'left',
    padding: '4px 0',
    borderBottom: '0.5px solid #000',
    fontSize: '9px',
    color: '#444',
    fontWeight: 400,
    fontStyle: 'italic',
  },
  detailTh: {
    textAlign: 'right',
    padding: '4px 0',
    borderBottom: '0.5px solid #000',
    fontSize: '9px',
    color: '#444',
    fontWeight: 400,
    fontStyle: 'italic',
  },
  detailTdLeft: {
    textAlign: 'left',
    padding: '4px 0',
    fontSize: '11px',
  },
  detailTd: {
    textAlign: 'right',
    padding: '4px 0',
    fontSize: '11px',
  },
  detailTdBold: {
    textAlign: 'right',
    padding: '4px 0',
    fontSize: '11px',
    fontWeight: 700,
  },
  detailSubTdLeft: {
    textAlign: 'left',
    padding: '3px 0 3px 16px',
    fontSize: '10px',
    color: '#444',
  },
  detailSubTd: {
    textAlign: 'right',
    padding: '3px 0',
    fontSize: '10px',
    color: '#444',
  },
  totalRow: {
    borderTop: '0.5px solid #000',
  },
  totalTd: {
    padding: '5px 0',
    fontSize: '11px',
  },
  incomeLabel: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    marginTop: '12px',
    marginBottom: '4px',
  },
  netLine: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderTop: '1px solid #000',
    marginTop: '6px',
    fontSize: '12px',
  },

  footer: {
    marginTop: '24px',
    paddingTop: '8px',
    borderTop: '0.5px solid #000',
    fontSize: '9px',
    color: '#666',
    fontStyle: 'italic',
    textAlign: 'center',
  },
}
